<template>
  <div>
    <v-btn
      v-if="isAdmin"
      class="mb-4"
      rounded="lg"
      @click="createDepartment(0)">
      <v-icon class="mr-1">mdi-plus</v-icon>
      <span>Створити департамент</span>
    </v-btn>

    <div class="d-flex gap-3 mb-2">
      <v-btn color="secondary" small @click="expandAll">
        <span class="mr-1">Розгорнути</span>
        <v-icon color="white">{{ 'mdi-plus' }}</v-icon>
      </v-btn>
      <v-btn color="secondary" small @click="collapseAll">
        <span class="mr-1">Згорути</span>
        <v-icon color="white">{{ 'mdi-minus' }}</v-icon>
      </v-btn>
    </div>
    <div v-if="!$loading.isLoading('getDepartmentList')">
      <tree-table
        v-model:expanded="expanded"
        :headers="companyStructureHeaders"
        :items="formattedDepartmentList"
        item-value="id">
        <template #item.name="{ item, props }">
          <div
            class="d-flex align-center gap-3"
            v-bind="props"
            @click="console.log(item)">
            <span v-if="item.type === 'supervisor'" class="disabled">
              Супервайзер
            </span>
            <span
              :class="{
                'font-weight-bold': item.type === 'department',
                link: item.type !== 'department',
              }"
              @click="navigateToUser(item)">
              {{ item.name }}
            </span>
          </div>
        </template>
        <template #item.actions="{ item, props }">
          <div
            v-if="item.type === 'department'"
            style="width: min-content"
            v-bind="props">
            <b-action-menu
              :actions="[
                {
                  title: 'Редагувати',
                  icon: 'mdi-pencil',
                  action: () => updateDepartment(item.id),
                  disabled: !isAdmin,
                  iconProps: {
                    color: 'blue-darken-3',
                  },
                },
                {
                  title: 'Видалити департамент',
                  icon: 'mdi-delete-forever',
                  action: () => deleteDepartment(item.id),
                  disabled: !isAdmin,
                  listItemProps: {
                    class: 'text-error',
                  },
                  iconProps: {
                    color: 'error',
                  },
                },
              ]">
            </b-action-menu>
          </div>
        </template>
      </tree-table>
    </div>
    <div v-else>
      <skeleton></skeleton>
    </div>
  </div>
</template>

<script lang="ts">
import TreeTable from '@/components/tree-table/Index.vue'
import Skeleton from '@/components/Skeleton.vue'
import { useStore } from 'vuex'
import { computed, ref, watch } from 'vue'
import { companyStructureHeaders } from '@/utils/tableHeaders'
import { useDepartmentList } from './index'
import { getPersonName } from 'best-modules/utils'
import { BActionMenu } from 'best-modules/components'
import { openUserCardDialog } from '@/utils/userCardDialog'

export default {
  name: 'TableView',
  components: { BActionMenu, TreeTable, Skeleton },
  data() {
    return { companyStructureHeaders }
  },
  setup() {
    const store = useStore()
    const isAdmin = computed(() => store.state.user?.userData?.roleId === 1)
    const expandedKeys = ref<any[]>([])
    const expanded = ref<any[]>([])
    const {
      departmentList,
      createDepartment,
      updateDepartment,
      deleteDepartment,
    } = useDepartmentList()

    const prepareEntity = (
      entity: Record<string, any>,
      type: 'department' | 'supervisor' | 'employee' = 'department'
    ): any => {
      const isDep = type === 'department'
      const isSup = type === 'supervisor'

      const getName = () => {
        if (isDep) {
          return entity.name
        } else {
          const getPost = () => `(${entity.user.post || 'Посаду не вказано'})`

          return `${getPersonName(entity.user)} ${getPost()}`
        }
      }

      const items = []
      if (isDep) {
        items.push(
          ...(entity.allChildren || []).map((d: any) => prepareEntity(d))
        )
        if (entity.supervisor) {
          items.push(prepareEntity(entity.supervisor, 'supervisor'))
        }
        items.push(
          ...(entity.employees || []).map((e: any) =>
            prepareEntity(e, 'employee')
          )
        )
      }

      return {
        ...entity,
        name: getName(),
        isSupervisor: isSup,
        items: items,
        type,
      }
    }

    const formattedDepartmentList = computed(() => {
      return departmentList.value.map(d => prepareEntity(d, 'department'))
    })

    const setExpandedKeys = (entity: any) => {
      if (!entity) return
      entity.forEach((d: any) => {
        expandedKeys.value.push(d.id)
        setExpandedKeys(d.allChildren)
        setExpandedKeys(d.employees)
        d.supervisor && expandedKeys.value.push(d.supervisor.id)
      })
    }

    const expandAll = () => {
      expanded.value = [...expandedKeys.value]
    }
    const collapseAll = () => {
      expanded.value = []
    }

    const navigateToUser = (item: any) => {
      if (item.type !== 'department') {
        openUserCardDialog({
          userId: item.id,
          mode: 'employee',
          context: 'list',
          title: item.name?.replace(/\s+\(.+\)$/, ''),
        })
      }
    }

    watch(
      departmentList,
      list => {
        setExpandedKeys(list)
      },
      { immediate: true }
    )

    return {
      isAdmin,
      departmentList,
      formattedDepartmentList,
      expandAll,
      collapseAll,
      navigateToUser,
      expanded,
      createDepartment,
      updateDepartment,
      deleteDepartment,
    }
  },
}
</script>

<style lang="scss" scoped></style>
